import {
  ResolvedPropertySharingLink,
  MissingPropertySharingLink,
} from 'src/api/generated';
import { useState } from 'react';
import { useProperty } from '../../hooks/api/catalog/properties';
import { useHasPermissions } from '../../hooks/auth';
import { PropertyPermission } from '../../types/permissions.type';
import type { SharePropertyPickerProps } from '../../types/catalog/property-sharing.types';
import { useI18n } from '../../hooks/provider-bounded/i18n';
import Button from '../atoms/buttons/button';
import { Text } from '../atoms/text';
import Modal from './modals/modal';
import { PropertySelector } from './property-selector';

/** Select an authorized property and insert its prepared link into the existing Comos draft. */
export function SharePropertyPicker({
  disabled,
  open,
  onClose,
  onInsert,
}: SharePropertyPickerProps) {
  const { t } = useI18n();
  const { hasPermission: canReadAll } = useHasPermissions(
    PropertyPermission.Read
  );
  const { hasPermission: canReadScoped } = useHasPermissions(
    PropertyPermission.ReadScoped
  );
  const canRead = canReadAll || canReadScoped;
  const [propertyId, setPropertyId] = useState<number | null>(null);
  const selected = useProperty(
    propertyId ?? -1,
    canRead && open && propertyId !== null
  );
  const link = !selected.error ? selected.property?.sharingLink : undefined;
  if (!canRead) return null;
  return (
    <>
      <Modal
        isOpen={open}
        onClose={onClose}
        header={<Text size="h5">{t('propertyLinks.shareTitle')}</Text>}
        body={
          <div className="w-full space-y-4 p-2">
            <Text size="small" color="secondary">
              {t('propertyLinks.insertHelp')}
            </Text>
            <PropertySelector
              value={propertyId}
              onChange={setPropertyId}
              disabled={disabled}
            />
            {selected.loading && <Text>{t('propertyLinks.loading')}</Text>}
            {link?.status === ResolvedPropertySharingLink.status.RESOLVED && (
              <Text className="break-all font-mono">{link.url}</Text>
            )}
            {link?.status === MissingPropertySharingLink.status.NONE && (
              <Text>{t('propertyLinks.noLink')}</Text>
            )}
            {(Boolean(selected.error) ||
              (propertyId !== null && !selected.loading && !link)) && (
              <div role="alert">
                <Text>{t('propertyLinks.resolutionError')}</Text>
                <Button onClick={() => void selected.refetch()}>
                  {t('propertyLinks.retry')}
                </Button>
              </div>
            )}
          </div>
        }
        footer={
          <div className="flex justify-end p-4">
            <Button
              disabled={
                disabled ||
                link?.status !== ResolvedPropertySharingLink.status.RESOLVED ||
                selected.loading
              }
              onClick={() => {
                if (
                  link?.status === ResolvedPropertySharingLink.status.RESOLVED
                ) {
                  onInsert(link.url);
                  onClose();
                }
              }}
            >
              {t('propertyLinks.insert')}
            </Button>
          </div>
        }
      />
    </>
  );
}
